import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUrlParam } from "@/lib/useUrlParam";

export type UrlSearch = ReturnType<typeof useUrlSearch>;

/**
 * A list's search, kept in the URL (`useUrlParam`), so it is still there
 * when the operator comes back from the row it led to.
 * What is typed stays a draft until submitted: searching on every keystroke
 * would send a request per letter and page through half-typed addresses.
 *
 * Sent trimmed and otherwise as typed: how a list reads a search is the
 * worker's rule, not this box's. A search that is empty once trimmed clears
 * the parameter rather than searching for the empty string.
 */
export function useUrlSearch(key = "q") {
  const [value, setValue] = useUrlParam(key);
  const [draft, setDraft] = React.useState(value);
  React.useEffect(() => setDraft(value), [value]);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setValue(draft.trim());
  }

  return { value, draft, setDraft, submit };
}

export function SearchBox({
  search,
  label,
  placeholder,
  className = "w-56",
}: {
  search: UrlSearch;
  /** Said to a screen reader; the placeholder is only a hint. */
  label: string;
  placeholder: string;
  /** The box's width, sized for what this list is searched by. */
  className?: string;
}) {
  return (
    <form onSubmit={search.submit} className="flex items-center gap-2">
      <Input
        size="sm"
        className={className}
        placeholder={placeholder}
        aria-label={label}
        value={search.draft}
        onChange={(e) => search.setDraft(e.target.value)}
      />
      <Button size="sm" variant="secondary" type="submit">
        Find
      </Button>
    </form>
  );
}
